import { CHANNELS, type EmailType } from './waterfallTypes';
import type { RepairableWaterfallRow } from '../wire-schemas';
import { isValidChannel } from './template-admin-rows';

const EMAIL_TYPE_OPTIONS: { value: EmailType; label: string }[] = [
  { value: 'manual', label: 'Manual' },
  { value: 'ai_generated', label: 'AI Generated' },
  { value: 'ai_generated_with_base', label: 'AI + Base' },
];

// ── Cell Inspector ───────────────────────────────────────────────────────────

export function CellInspector({
  cell,
  statusColumn,
  onChange,
}: {
  cell: RepairableWaterfallRow['cells'][string];
  statusColumn: string;
  onChange: (updated: RepairableWaterfallRow['cells'][string]) => void;
}) {
  const isInvalidChannel = !!cell.channel && !isValidChannel(cell.channel);

  const senderLabel = cell.sender
    ? cell.sender.type === 'token'
      ? cell.sender.token ?? 'token'
      : cell.sender.email ?? 'static'
    : '—';

  return (
    <div className="border border-nightshade-200 dark:border-nightshade-700 rounded-lg p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-nightshade-700 dark:text-nightshade-200">{statusColumn}</span>
        <label className="flex items-center gap-1.5 text-[11px] text-nightshade-500">
          <input
            type="checkbox"
            checked={cell.enabled}
            onChange={e => onChange({ ...cell, enabled: e.target.checked })}
            className="rounded border-nightshade-300 text-blueberry-600 focus:ring-blueberry-500"
          />
          Enabled
        </label>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {/* Channel */}
        <div>
          <label className="text-[10px] font-mono uppercase text-nightshade-400">Channel</label>
          <select
            value={cell.channel}
            onChange={e => onChange({ ...cell, channel: e.target.value })}
            className={`w-full mt-0.5 px-2 py-1 text-xs rounded-md border ${
              isInvalidChannel
                ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                : 'border-nightshade-200 dark:border-nightshade-600 bg-white dark:bg-nightshade-800 text-nightshade-900 dark:text-nightshade-50'
            }`}
          >
            {isInvalidChannel && (
              <option value={cell.channel} className="text-red-600">
                {cell.channel} (invalid)
              </option>
            )}
            {CHANNELS.map(ch => (
              <option key={ch.value} value={ch.value}>{ch.label}</option>
            ))}
          </select>
          {isInvalidChannel && (
            <p className="text-[10px] text-red-600 dark:text-red-400 mt-0.5">
              Invalid value: "{cell.channel}" — select a valid channel
            </p>
          )}
        </div>

        {/* Email Type */}
        <div>
          <label className="text-[10px] font-mono uppercase text-nightshade-400">Email Type</label>
          <select
            value={cell.email_type ?? 'manual'}
            onChange={e => onChange({ ...cell, email_type: e.target.value as EmailType })}
            className="w-full mt-0.5 px-2 py-1 text-xs rounded-md border border-nightshade-200 dark:border-nightshade-600 bg-white dark:bg-nightshade-800 text-nightshade-900 dark:text-nightshade-50"
          >
            {EMAIL_TYPE_OPTIONS.map(et => (
              <option key={et.value} value={et.value}>{et.label}</option>
            ))}
          </select>
        </div>

        {/* Flow ID */}
        <div>
          <label className="text-[10px] font-mono uppercase text-nightshade-400">Flow ID</label>
          <input
            type="text"
            value={cell.flow_id ?? ''}
            onChange={e => onChange({ ...cell, flow_id: e.target.value })}
            className="w-full mt-0.5 px-2 py-1 text-xs rounded-md border border-nightshade-200 dark:border-nightshade-600 bg-white dark:bg-nightshade-800 text-nightshade-900 dark:text-nightshade-50"
          />
        </div>

        {/* Sender */}
        <div>
          <label className="text-[10px] font-mono uppercase text-nightshade-400">Sender</label>
          <p className="mt-0.5 text-xs text-nightshade-600 dark:text-nightshade-300 truncate">{senderLabel}</p>
        </div>
      </div>

      {/* Copy preview */}
      {(cell.subject || cell.body) && (
        <div className="mt-1">
          <label className="text-[10px] font-mono uppercase text-nightshade-400">Copy</label>
          {cell.subject && (
            <p className="text-[11px] text-nightshade-600 dark:text-nightshade-300 truncate">
              <span className="font-medium">Subj:</span> {cell.subject}
            </p>
          )}
          {cell.body && (
            <p className="text-[11px] text-nightshade-500 dark:text-nightshade-400 truncate">
              {cell.body.slice(0, 100)}{cell.body.length > 100 ? '…' : ''}
            </p>
          )}
          {cell.copy_status && (
            <span className={`inline-block mt-1 px-1.5 py-0.5 text-[10px] rounded-full font-medium ${
              cell.copy_status === 'ready'
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
            }`}>
              {cell.copy_status}
            </span>
          )}
        </div>
      )}

      {/* Handoff */}
      {cell.has_claydr_handoff && (
        <p className="text-[11px] text-nightshade-500">ClayDR Handoff: {cell.handoff_hours}h</p>
      )}
    </div>
  );
}
